import { useEffect, useMemo, useState } from "react";

import { useCaseDetailData } from "@/components/case-checking/CaseDetailDataContext";
import {
  buildRuleOverrideMap,
  type RuleOverrideMap,
} from "@/components/case-checking/humanRuleReviewCascade";
import {
  getActivePsRuleHumanReviewRun,
  listProblemStatementRuns,
  type PsRuleHumanReviewRun,
} from "@/services/api/case-checking";

function useStandaloneHumanRuleReviews(caseId: string, enabled: boolean) {
  const [reviewRun, setReviewRun] = useState<PsRuleHumanReviewRun | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    listProblemStatementRuns(caseId, 1)
      .then((runs) => {
        const psRun = runs[0];
        if (!psRun) return null;
        return getActivePsRuleHumanReviewRun(caseId, psRun.id, false);
      })
      .then((active) => {
        if (cancelled) return;
        setReviewRun(active);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load rule reviews");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [caseId, enabled]);

  return { reviewRun, loading, error };
}

export function useHumanRuleReviews(caseId: string) {
  const shared = useCaseDetailData();
  const useShared = Boolean(shared && shared.caseId === caseId);
  const standalone = useStandaloneHumanRuleReviews(caseId, !useShared);

  const detailData = useShared && shared ? shared : null;
  const reviewRun = detailData ? detailData.psRuleReviewRun : standalone.reviewRun;
  const loading = detailData
    ? detailData.psRunLoading || detailData.psRuleReviewLoading
    : standalone.loading;
  const error = detailData ? detailData.psRuleReviewError ?? detailData.psRunError : standalone.error;

  const overridesByKey: RuleOverrideMap = useMemo(
    () => buildRuleOverrideMap(reviewRun?.reviews ?? []),
    [reviewRun?.reviews],
  );

  return {
    loading,
    error,
    reviewRun,
    overridesByKey,
    reloadToken: reviewRun?.id,
  };
}
